import React, { useCallback, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import SkipNextIcon from '@material-ui/icons/SkipNext';
import SkipPreviousIcon from '@material-ui/icons/SkipPrevious';
import PauseIcon from '@material-ui/icons/Pause';
import QueueIcon from '@material-ui/icons/Queue';


const iconStyle = { fontSize: '35px', color: 'white' };
const RETRY_ACTIVE_SESSION_CHECKS_AFTER_MS = 500;
const TRIES = 15;

function CastVideoPlayer(props) {
  const castInstance = window.cast?.framework?.CastContext?.getInstance();
  const { segments, user, setVideoIsCasting, getVideoSrc } = props;
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCast, setIsCast] = useState(false);
  const [isCasting, setIsCasting] = useState(false);
  const [media, setMedia] = useState(undefined);
  const [count, setCount] = useState(0);
  const [intervalID, setIntervalID] = useState(null);
  const countRef = useRef(null);
  countRef.current = count;

  const initializeCastApi = useCallback(() => {
    if (castInstance) {
      castInstance.setOptions({
        receiverApplicationId: window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
        autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      });
    }
  }, []);

  const checkForMediaSession = useCallback(() => {
    if (media) {
      setVideoIsCasting(true);
      setIsCasting(true);
    }
    if (castInstance?.getCurrentSession?.()) {
      window.castChromeContext = castInstance;
      const mediaSession = window.castChromeContext.getCurrentSession().getMediaSession();
      setMedia(mediaSession);
      setIsCasting(true);
      setVideoIsCasting(true);
    }
  }, [media]);

  const CHECK_FOR_SESSION_IN_PROGRESS = setInterval(() => {
    if (countRef.current >= TRIES) {
      clearInterval(CHECK_FOR_SESSION_IN_PROGRESS);
    } else {
      setCount((prev) => prev + 1);
      checkForMediaSession();
    }
    if (!intervalID) {
      setIntervalID(CHECK_FOR_SESSION_IN_PROGRESS);
    }
  }, RETRY_ACTIVE_SESSION_CHECKS_AFTER_MS);

  const setSession = useCallback(() => {
    const context = castInstance;
    let mediaSession = undefined;
    context.addEventListener(
      window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
      (event) => {
        switch (event.sessionState) {
          case window.cast.framework.SessionState.SESSION_STARTED:
            setIsCast(true);
            window.castChromeContext = context;
            sessionStorage.setItem('cast-session', JSON.stringify(context));
            setVideoIsCasting(true);
            break;
          case window.cast.framework.SessionState.SESSION_RESUMED:
            setIsCast(true);
            window.castChromeContext = context;
            setVideoIsCasting(true);
            mediaSession = window.castChromeContext.getCurrentSession().getMediaSession();
            setMedia(mediaSession);
            break;
          case window.cast.framework.SessionState.SESSION_ENDED:
            setVideoIsCasting(false);
            setIsCast(false);
            window.castChromeContext = undefined;
            setMedia(undefined);
            setIsCasting(false);
            break;
          default:

        }
      });
  }, []);

  useEffect(() => {
    if (window.chrome?.cast && user.authenticated) {
      const castBtn = document.createElement('google-cast-launcher');
      castBtn.addEventListener('click', setSession);
      const videoPlayerContainer = document.getElementById('video-player-container');
      videoPlayerContainer.appendChild(castBtn);
      if (window.cast.framework) {
        initializeCastApi();
      }
    }

    return () => {
      clearInterval(CHECK_FOR_SESSION_IN_PROGRESS);
    };
  }, []);

  const addItemsToQueue = useCallback(() => {
    const castMediaObj = window.castChromeContext;
    const session = castMediaObj.getCurrentSession();
    if (castInstance) {
      const mediaLoadRequest = new window.chrome.cast.media.MediaInfo(getVideoSrc(segments[0].clip, 'Auto'));
      const request = new window.chrome.cast.media.LoadRequest(mediaLoadRequest);
      let sources = [];
      const Media = new window.chrome.cast.media.Media(session, 1);
      for (const segment of segments) {
        const { clip } = segment;
        const src = getVideoSrc(clip, 'SIZE_720');
        const mediaInfo = new window.chrome.cast.media.MediaInfo(src);
        mediaInfo.contentUrl = src;
        Media.media = mediaInfo;
        const queueItem = new window.chrome.cast.media.QueueItem(mediaInfo);
        sources.push(queueItem);
      }
      Media.sessionId = session;
      Media.items = sources;
      const queueData = new window.chrome.cast.media.QueueData();
      queueData.items = sources;
      sources = [];
      request.queueData = queueData;
      request.autoplay = true;
      session.loadMedia(request).then(
        () => {
          const mediaSession = window.castChromeContext.getCurrentSession().getMediaSession();
          setMedia(mediaSession);
        }).catch((errorCode) => {
        console.error(errorCode);
      });
      setIsPlaying(true);
    }
  }, [media]);

  const onTogglePlayOrPause = useCallback(async() => {
    if (media) {
      if (!isPlaying) {
        const p = new window.chrome.cast.media.PlayRequest();
        await media.play(p);
        setIsPlaying(true);
      } else {
        const pause = new window.chrome.cast.media.PauseRequest();
        await media.pause(pause);
        setIsPlaying(false);
      }
    }
  }, [media, isPlaying]);

  const onPlayNext = useCallback(async() => {
    if (media) {
      await media.queueNext();
    }
  }, [media]);

  const onPlayPrev = useCallback(async() => {
    if (media) {
      await media.queuePrev();
    }
  }, [media]);

  return (
    <div className="chrome-cast-player" id="chrome-cast-controls">
      {(isCast || isCasting) && (
        <div className="controls">
          <button onClick={onTogglePlayOrPause}>
            {
              !isPlaying ? <PlayArrowIcon style={iconStyle} /> : <PauseIcon style={iconStyle} />
            }
          </button>
          <button onClick={onPlayPrev}><SkipPreviousIcon style={iconStyle} /></button>
          <button onClick={onPlayNext}><SkipNextIcon style={iconStyle} /></button>
          <button onClick={addItemsToQueue}><QueueIcon style={iconStyle} /></button>
        </div>
      )}
    </div>
  );
}

CastVideoPlayer.propTypes = {
  getVideoSrc: PropTypes.func.isRequired,
  segments: PropTypes.array.isRequired,
  setVideoIsCasting: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
};

export default CastVideoPlayer;
